<template>
  <div class="auction-status-block" v-if="!auctionStore.user?.is_admin && auctionStatus === AuctionStatuses.ACTIVE">
    <div class="auction-status-wrapper">
      <timer-live-auction-begin />
    </div>
  </div>
  <div v-else :class="['lot-timeline-messages', { 'pinned-message': auctionStore.pinMessage?.id }]">
    <Teleport :disabled="props.disableMobileTeleport || !isScreenL || !hasMobileHistoryTarget" to="#history-mobile-wrapper .lot-timeline-messages" defer>
      <custom-scrollbar class="custom-scrollbar controller-scrollbar" :class="{ 'not-scroller': historyLogs?.length < 4 }" @scrollerComponent="onInitScroll">
        <pinned-message :hidden="user?.is_admin || isScreenL" />
        <transition-group name="history-messages" :css="isPageVisible">
          <div :class="[
            'event-message',
            {
              'js-secondary': log.is_recent,
              'your-bid': log.is_your_bid,
              clarified: log.type === LogTypes.AUTO_BID,
              primary: log.type === LogTypes.SOLD || log.type === LogTypes.UNSOLD
            }
          ]"
               v-for="log in historyLogs"
               :key="log.id"
               v-html="log.text"
          ></div>
        </transition-group>
      </custom-scrollbar>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch, withDefaults } from "vue";
  import CustomScrollbar from "@/components/_partials/CustomScrollbar.vue";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import simplebar from "simplebar-vue";
  import { BaseIcon, KeepIcon } from "@/components/icons";
  import { IMessage } from "@/models";
  import { LogTypes, AuctionStatuses, LotStatuses } from "@/enum";
  import PinnedMessage from "@/components/_partials/PinnedMessage.vue";
  import { trans } from "@/utils/trans";
  import { isScreenL } from "@/utils/media-size";
  import { isPageVisible } from "@/utils/page-visibility";
  import TimerLiveAuctionBegin from "@/components/_partials/TimerLiveAuctionBegin.vue";

  const props = withDefaults(defineProps<{
    disableMobileTeleport?: boolean;
  }>(), {
    disableMobileTeleport: false
  });

  const emitter = inject("emitter");
  const auctionStore = useAuctionStore();
  const container = ref<typeof simplebar>();
  const newMessage = ref<IMessage | null>(null);
  const resetPinedMessageTimerId = ref<number | null>(null);
  const historyLogs = computed(() => auctionStore.historyLogs);
  const auctionStatus = computed(() => auctionStore.auction.status);
  const user = computed(() => auctionStore.user);
  const activeLotId = computed(() => auctionStore.activeLot?.id ?? auctionStore.fakeActiveLot?.id ?? null);
  const liveLotId = computed(() => auctionStore.lots.find((item) => item.status === LotStatuses.LIVE)?.id ?? null);
  const hasMobileHistoryTarget = ref(false);
  let mobileHistoryTargetObserver: MutationObserver | null = null;

  const syncMobileHistoryTarget = () => {
    hasMobileHistoryTarget.value = !!document.querySelector("#history-mobile-wrapper .lot-timeline-messages");
  };

  watch(
    () => [isScreenL.value, activeLotId.value, liveLotId.value, auctionStatus.value],
    async () => {
      await nextTick();
      syncMobileHistoryTarget();
    },
    { immediate: true }
  );

  watch(
    () => historyLogs.value.length,
    async () => {
      await nextTick();
      syncMobileHistoryTarget();
    }
  );

  watch(
    historyLogs,
    () => {
      setTimeout(() => {
        const messages = document.querySelectorAll(".event-message.js-secondary");
        messages.forEach((message) => {
          message.classList.add("secondary");
        });
      }, 100);
      setTimeout(() => {
        const messages = document.querySelectorAll(".event-message.js-secondary");
        messages.forEach((message) => {
          message.classList.remove("secondary", "js-secondary");
        });
      }, 1500);
      nextTick(() => {
        if (container.value) {
          container.value.scrollTop = container.value.scrollHeight;
        }
      });
    },
    { deep: true }
  );

  // watch(activeLot, () => {
  //   refreshLogs();
  // });
  //
  // onMounted(() => {
  //   refreshLogs();
  // });
  //
  // const refreshLogs = () => {
  //   console.log("refreshLogs");
  //   auctionStore.resetHistoryLog();
  //
  //   if (activeLot.value) {
  //     console.log("activeLot.value.logs", activeLot.value.logs);
  //     activeLot.value.logs.forEach((item) => {
  //       auctionStore.addHistoryLog(item);
  //     });
  //   }
  // };

  onMounted(() => {
    nextTick(() => {
      syncMobileHistoryTarget();
      mobileHistoryTargetObserver = new MutationObserver(() => {
        syncMobileHistoryTarget();
      });
      mobileHistoryTargetObserver.observe(document.body, { childList: true, subtree: true });
      if (isScreenL.value && container.value) {
        container.value.scrollTop = container.value.scrollHeight;
      }
    });
  });

  onBeforeUnmount(() => {
    mobileHistoryTargetObserver?.disconnect();
  });

  const onInitScroll = (scrollbar: typeof simplebar) => {
    container.value = scrollbar.value.scrollElement;
  };

  emitter?.on("on:chat-new-message", (message: IMessage) => {
    newMessage.value = message;

    if (resetPinedMessageTimerId.value) {
      clearTimeout(resetPinedMessageTimerId.value);
    }

    resetPinedMessageTimerId.value = setTimeout(() => {
      newMessage.value = null;
    }, (auctionStore.newMessagePinSeconds || 60) * 1000);
  });
</script>
